import { createOptimizelyConfig } from './optimizely_config';
import { toDatafile, tryCreatingProjectConfig } from './project_config';
import { ServiceState, BaseService } from '../service';
import { EventEmitter } from '../utils/event_emitter/event_emitter';
import { SERVICE_FAILED_TO_START, SERVICE_STOPPED_BEFORE_RUNNING, } from '../service';
export const NO_SDKKEY_OR_DATAFILE = 'sdkKey or datafile must be provided';
export const GOT_INVALID_DATAFILE = 'got invalid datafile';
import { sprintf } from '../utils/fns';
/**
 * ProjectConfigManager provides project config objects via its methods
 * getConfig and onUpdate. It uses a DatafileManager to fetch datafile if provided.
 * It is responsible for parsing and validating datafiles, and converting datafile
 * string into project config objects.
 * @param {ProjectConfigManagerConfig}    config
 */
export const LOGGER_NAME = 'ProjectConfigManager';
export class ProjectConfigManagerImpl extends BaseService {
    constructor(config) {
        super();
        this.eventEmitter = new EventEmitter();
        this.jsonSchemaValidator = config.jsonSchemaValidator;
        this.datafile = config.datafile;
        this.datafileManager = config.datafileManager;
        if (config.logger) {
            this.setLogger(config.logger);
        }
    }
    setLogger(logger) {
        var _a;
        this.logger = logger;
        this.logger.setName(LOGGER_NAME);
        (_a = this.datafileManager) === null || _a === void 0 ? void 0 : _a.setLogger(logger.child());
    }
    start() {
        var _a, _b, _c;
        if (!this.isNew()) {
            return;
        }
        this.state = ServiceState.Starting;
        if (!this.datafile && !this.datafileManager) {
            this.handleInitError(new Error(NO_SDKKEY_OR_DATAFILE));
            return;
        }
        if (this.datafile) {
            this.handleNewDatafile(this.datafile, true);
        }
        (_a = this.datafileManager) === null || _a === void 0 ? void 0 : _a.start();
        // This handles the case where the datafile manager starts successfully. The 
        // datafile manager will only start successfully when it has downloaded a datafile,
        // an will fire an onUpdate event.
        (_b = this.datafileManager) === null || _b === void 0 ? void 0 : _b.onUpdate(this.handleNewDatafile.bind(this));
        // If the datafile manager runs successfully, it will emit a onUpdate event. We can
        // handle the success case in the onUpdate handler. Hanlding the error case in the
        // catch callback
        (_c = this.datafileManager) === null || _c === void 0 ? void 0 : _c.onRunning().catch((err) => {
            this.handleDatafileManagerError(err);
        });
    }
    makeDisposable() {
        var _a;
        super.makeDisposable();
        (_a = this.datafileManager) === null || _a === void 0 ? void 0 : _a.makeDisposable();
    }
    handleInitError(error) {
        var _a, _b;
        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(error);
        this.state = ServiceState.Failed;
        (_b = this.datafileManager) === null || _b === void 0 ? void 0 : _b.stop();
        this.startPromise.reject(error);
        this.stopPromise.reject(error);
    }
    handleDatafileManagerError(err) {
        var _a;
        (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error(SERVICE_FAILED_TO_START, 'DatafileManager', err.message);
        // If datafile manager onRunning() promise is rejected, and the project config manager 
        // is still in starting state, that means a datafile was not provided in cofig or was invalid, 
        // otherwise the state would have already been set to running synchronously.
        // In this case, we cannot recover.
        if (this.isStarting()) {
            this.handleInitError(new Error(sprintf(SERVICE_FAILED_TO_START, 'DatafileManager', err.message)));
        }
    }
    /**
     * Handle new datafile by attemping to create a new Project Config object. If successful and
     * the new config object's revision is newer than the current one, sets/updates the project config
     * and emits onUpdate event. If unsuccessful,
     * the project config and optimizely config objects will not be updated. If the error
     * is fatal, handleInitError will be called.
     */
    handleNewDatafile(newDatafile, fromConfig = false) {
        var _a, _b;
        if (this.isDone()) {
            return;
        }
        try {
            const config = tryCreatingProjectConfig({
                datafile: newDatafile,
                jsonSchemaValidator: this.jsonSchemaValidator,
                logger: this.logger,
            });
            if (this.isStarting()) {
                this.state = ServiceState.Running;
                this.startPromise.resolve();
            }
            if (((_a = this.projectConfig) === null || _a === void 0 ? void 0 : _a.revision) !== config.revision) {
                this.projectConfig = config;
                this.optimizelyConfig = undefined;
                this.eventEmitter.emit('update', config);
            }
        }
        catch (err) {
            (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error(err);
            // if the state is starting and no datafileManager is provided, we cannot recover.
            // If the state is starting and the datafileManager has emitted a datafile,
            // that means a datafile was not provided in config or an invalid datafile was provided,
            // otherwise the state would have already been set to running synchronously.
            // If the first datafile emitted by the datafileManager is invalid, 
            // we consider this to be an initialization error as well.
            const fatalError = (this.isStarting() && !this.datafileManager) ||
                (this.isStarting() && !fromConfig);
            if (fatalError) {
                this.handleInitError(new Error(GOT_INVALID_DATAFILE));
            }
        }
    }
    getConfig() {
        return this.projectConfig;
    }
    getOptimizelyConfig() {
        if (!this.optimizelyConfig && this.projectConfig) {
            this.optimizelyConfig = createOptimizelyConfig(this.projectConfig, toDatafile(this.projectConfig), this.logger);
        }
        return this.optimizelyConfig;
    }
    /**
     * Add a listener for project config updates. The listener will be called
     * whenever this instance has a new project config object available.
     * Returns a dispose function that removes the subscription
     * @param  {Function} listener
     * @return {Function}
     */
    onUpdate(listener) {
        return this.eventEmitter.on('update', listener);
    }
    stop() {
        if (this.isDone()) {
            return;
        }
        if (this.isNew() || this.isStarting()) {
            this.startPromise.reject(new Error(sprintf(SERVICE_STOPPED_BEFORE_RUNNING, 'ProjectConfigManager')));
        }
        this.state = ServiceState.Stopping;
        this.eventEmitter.removeAllListeners();
        if (!this.datafileManager) {
            this.state = ServiceState.Terminated;
            this.stopPromise.resolve();
            return;
        }
        this.datafileManager.stop();
        this.datafileManager.onTerminated().then(() => {
            this.state = ServiceState.Terminated;
            this.stopPromise.resolve();
        }).catch((err) => {
            this.state = ServiceState.Failed;
            this.stopPromise.reject(err);
        });
    }
}
export const __platforms = ['__universal__'];
//# sourceMappingURL=project_config_manager.js.map